import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type CatalogPropertyFileImportBodyParams,
  CatalogPropertiesService,
  type CatalogProperty,
  type CatalogPropertyBulkImportBodyParams,
  type CatalogPropertyBulkImportResponse,
  type CatalogPropertyCreateBodyParams,
  type CatalogPropertyUpdateBodyParams,
  IntegrationsPropertiesService,
  type PropertyRepairSyncResponse,
} from 'src/api/generated';
import { normalizeDecimalStrings } from 'src/modules/shared/utils/formatting/decimal-string.util';

const noop = (): void => undefined;

const PROPERTY_DECIMAL_STRING_FIELDS = [
  'salePriceAmount',
  'salePriceBaseAmount',
  'rentPriceAmount',
  'rentPriceBaseAmount',
  'areaTotal',
  'areaBuilt',
  'areaUsable',
  'areaOutdoor',
  'latitude',
  'longitude',
  'commonExpensesAmount',
  'commonExpensesBaseAmount',
] as const;

interface UsePropertyImportOptions {
  onSuccess?: (response: CatalogPropertyBulkImportResponse) => void;
  onError?: (error: Error) => void;
}

interface UsePropertyImportResponse {
  importProperties: (
    body: CatalogPropertyBulkImportBodyParams
  ) => Promise<CatalogPropertyBulkImportResponse>;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook for bulk importing catalog properties synchronously via the `/import` endpoint.
 * For Prop360-based imports we use the asynchronous job + enqueue flow instead.
 */
export const usePropertyImport = (
  options: UsePropertyImportOptions = {}
): UsePropertyImportResponse => {
  const queryClient = useQueryClient();
  const { onSuccess = noop, onError = noop } = options;

  const importPropertiesRequest = async (
    body: CatalogPropertyBulkImportBodyParams
  ): Promise<CatalogPropertyBulkImportResponse> => {
    return await CatalogPropertiesService.importProperties(body);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<
    CatalogPropertyBulkImportResponse,
    Error,
    CatalogPropertyBulkImportBodyParams
  >({
    mutationKey: ['catalog/properties/import'],
    mutationFn: importPropertiesRequest,
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['catalog/properties'] });
      onSuccess(response);
    },
    onError,
  });

  return {
    importProperties: mutateAsync,
    loading,
    error,
  };
};

interface UsePropertyMutationOptions {
  onSuccess?: (property: CatalogProperty) => void;
  onError?: (error: Error) => void;
}

interface UsePropertyMutationResponse {
  loading: boolean;
  error: Error | null;
}

export const useCreateProperty = (
  options: UsePropertyMutationOptions = {}
): UsePropertyMutationResponse & {
  createProperty: (
    data: CatalogPropertyCreateBodyParams
  ) => Promise<CatalogProperty>;
} => {
  const queryClient = useQueryClient();
  const { onSuccess = noop, onError = noop } = options;

  const createPropertyRequest = async (
    data: CatalogPropertyCreateBodyParams
  ): Promise<CatalogProperty> => {
    return await CatalogPropertiesService.createProperty(
      normalizeDecimalStrings(
        data,
        PROPERTY_DECIMAL_STRING_FIELDS
      ) as CatalogPropertyCreateBodyParams
    );
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<CatalogProperty, Error, CatalogPropertyCreateBodyParams>({
    mutationKey: ['catalog/properties/create'],
    mutationFn: createPropertyRequest,
    onSuccess: (property) => {
      void queryClient.invalidateQueries({ queryKey: ['catalog/properties'] });
      onSuccess(property);
    },
    onError,
  });

  return {
    createProperty: mutateAsync,
    loading,
    error,
  };
};

export const useUpdateProperty = (
  options: UsePropertyMutationOptions = {}
): UsePropertyMutationResponse & {
  updateProperty: (
    params: { id: number } & CatalogPropertyUpdateBodyParams
  ) => Promise<CatalogProperty>;
} => {
  const queryClient = useQueryClient();
  const { onSuccess = noop, onError = noop } = options;

  const updatePropertyRequest = async ({
    id,
    ...data
  }: {
    id: number;
  } & CatalogPropertyUpdateBodyParams): Promise<CatalogProperty> => {
    return await CatalogPropertiesService.updateProperty(
      id,
      normalizeDecimalStrings(
        data,
        PROPERTY_DECIMAL_STRING_FIELDS
      ) as CatalogPropertyUpdateBodyParams
    );
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<
    CatalogProperty,
    Error,
    { id: number } & CatalogPropertyUpdateBodyParams
  >({
    mutationKey: ['catalog/properties/update'],
    mutationFn: updatePropertyRequest,
    onSuccess: (property) => {
      void queryClient.invalidateQueries({ queryKey: ['catalog/properties'] });
      void queryClient.invalidateQueries({
        queryKey: ['catalog/properties', 'detail', property.id],
      });
      onSuccess(property);
    },
    onError: (error, variables) => {
      const status = 'status' in error ? error.status : undefined;
      if (status === 403 || status === 404) {
        queryClient.removeQueries({
          queryKey: ['catalog/properties', 'detail', variables.id],
        });
        void queryClient.invalidateQueries({
          queryKey: ['catalog/properties'],
        });
      }
      onError(error);
    },
  });

  return {
    updateProperty: mutateAsync,
    loading,
    error,
  };
};

export const useDeleteProperty = (
  options: UsePropertyMutationOptions = {}
): UsePropertyMutationResponse & {
  deleteProperty: (id: number) => Promise<CatalogProperty>;
} => {
  const queryClient = useQueryClient();
  const { onSuccess = noop, onError = noop } = options;

  const deletePropertyRequest = async (
    id: number
  ): Promise<CatalogProperty> => {
    return await CatalogPropertiesService.deleteProperty(id);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<CatalogProperty, Error, number>({
    mutationKey: ['catalog/properties/delete'],
    mutationFn: deletePropertyRequest,
    onSuccess: (property) => {
      void queryClient.invalidateQueries({ queryKey: ['catalog/properties'] });
      void queryClient.invalidateQueries({
        queryKey: ['catalog/properties', 'detail', property.id],
      });
      onSuccess(property);
    },
    onError,
  });

  return {
    deleteProperty: mutateAsync,
    loading,
    error,
  };
};

interface UsePropertyImportEnqueueOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UsePropertyImportEnqueueResponse {
  enqueuePropertyImport: (
    body: CatalogPropertyFileImportBodyParams
  ) => Promise<void>;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook for enqueuing an asynchronous provider import via the jobs pipeline.
 * This uses the unified `/api/catalog/properties/imports/enqueue` endpoint.
 */
export const usePropertyImportEnqueue = (
  options: UsePropertyImportEnqueueOptions = {}
): UsePropertyImportEnqueueResponse => {
  const { onSuccess = noop, onError = noop } = options;

  const enqueueRequest = async (
    body: CatalogPropertyFileImportBodyParams
  ): Promise<void> => {
    await CatalogPropertiesService.enqueuePropertyImport(body);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<void, Error, CatalogPropertyFileImportBodyParams>({
    mutationKey: ['catalog/properties/imports/enqueue'],
    mutationFn: enqueueRequest,
    onSuccess: () => {
      onSuccess();
    },
    onError,
  });

  return {
    enqueuePropertyImport: mutateAsync,
    loading,
    error,
  };
};

interface UsePropertyRepairSyncOptions {
  onSuccess?: (response: PropertyRepairSyncResponse) => void;
  onError?: (error: Error) => void;
}

interface UsePropertyRepairSyncResponse {
  repairSyncProperty: (
    propertyId: number
  ) => Promise<PropertyRepairSyncResponse>;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook for enqueuing a property-scoped source repair sync.
 */
export const useRepairPropertySync = (
  options: UsePropertyRepairSyncOptions = {}
): UsePropertyRepairSyncResponse => {
  const queryClient = useQueryClient();
  const { onSuccess = noop, onError = noop } = options;

  const repairSyncRequest = async (
    propertyId: number
  ): Promise<PropertyRepairSyncResponse> => {
    return IntegrationsPropertiesService.repairSyncProperty(propertyId);
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationKey: ['catalog/properties/repair-sync'],
    mutationFn: repairSyncRequest,
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: ['catalog/properties', 'detail', response.propertyId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['integrations', 'portal-publications'],
      });
      onSuccess(response);
    },
    onError,
  });

  return {
    repairSyncProperty: mutateAsync,
    loading: isPending,
    error: error instanceof Error ? error : null,
  };
};
